<template>
  <ClientOnly>
    <section
      v-if="programs?.Items?.length"
      class="programs-map-section">
      <div class="container">
        <h2 class="programs-map__title">
          {{ programs?.Title || title }}
        </h2>

        <!-- ── CARTE ────────────────────────────────── -->
        <div class="programs-map__map-wrapper">
          <div
            ref="mapEl"
            class="programs-map__map" />

          <div
            v-if="isGeocoding"
            class="programs-map__loader">
            <span>Localisation des programmes…</span>
          </div>
        </div>

        <!-- ── LISTE DES PROGRAMMES VISIBLES ────────── -->
        <div class="programs-map__list">
          <div class="programs-map__list-header">
            <span class="programs-map__list-count">
              {{ visiblePrograms.length }} / {{ totalPrograms }} programmes visibles
            </span>
          </div>

          <div
            v-if="visiblePrograms.length"
            class="programs-map__items">
            <UiProgramCard
              v-for="program in visiblePrograms"
              :key="program.Id"
              :program="program" />
          </div>

          <p
            v-else
            class="programs-map__empty">
            Aucun programme dans la zone visible. Dézoome pour en voir plus.
          </p>

          <!-- Programmes dont aucune adresse n'a pu être géocodée -->
          <div
            v-if="unlocatedPrograms.length"
            class="programs-map__unlocated">
            <span class="programs-map__unlocated-title"> Non localisés sur la carte : </span>
            <ul class="programs-map__unlocated-list">
              <li
                v-for="name in unlocatedPrograms"
                :key="name">
                {{ name }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import type { Map as LeafletMap, Marker as LeafletMarker, MarkerClusterGroup } from 'leaflet'
import type { ProgramCardInfos, ProgramLocationsList } from '~/types/school'
import { configureLeafletDefaultIcons } from '~/utils/leafletIcons'
// getAddressCoordinates est auto-importé depuis app/utils/geocoding.ts

// ── Types locaux ───────────────────────────────────────────
/**
 * Un "point" sur la carte = une (programme, adresse) localisée.
 * Un programme à N adresses produira N points distincts,
 * mais tous partagent le même cardInfos (et donc le même Id).
 */
interface ProgramPoint {
  PointId: number // index unique du point sur la carte
  ProgramId: number | string // Id stable du programme (pour dédup liste)
  CardInfos: ProgramCardInfos
  City: string
  Lat: number
  Lng: number
}

// ── Props ──────────────────────────────────────────────────
const props = withDefaults(
  defineProps<{
    programs: ProgramLocationsList | null
    title?: string
  }>(),
  { title: 'Où suivre ces programmes' },
)

// ── State ──────────────────────────────────────────────────
const mapEl = ref<HTMLElement | null>(null)
const map = shallowRef<LeafletMap | null>(null)
const markerCluster = shallowRef<MarkerClusterGroup | null>(null)

// Tous les points localisés (un par couple programme × adresse)
const points = ref<ProgramPoint[]>([])
// Programmes dont aucune adresse n'a pu être géocodée
const unlocatedPrograms = ref<string[]>([])
// Set des IDs de points actuellement dans la viewport
const visiblePointIds = ref<Set<number>>(new Set())
const isGeocoding = ref(false)

// ── Computed ───────────────────────────────────────────────
const totalPrograms = computed(() => props.programs?.Items?.length ?? 0)

/**
 * Regroupe les points visibles par Id de programme (dédup).
 * Retourne des ProgramCardInfos dont le LocationsString est
 * recalculé pour n'inclure que les villes actuellement visibles.
 * L'ordre de sortie suit l'ordre d'apparition dans props.programs.Items.
 */
const visiblePrograms = computed<ProgramCardInfos[]>(() => {
  // 1) Collecte : pour chaque programme visible, l'ensemble des villes visibles
  //    + une référence vers ses cardInfos d'origine.
  const byProgram = new Map<number | string, { CardInfos: ProgramCardInfos; cities: Set<string> }>()

  for (const p of points.value) {
    if (!visiblePointIds.value.has(p.PointId)) continue

    const entry = byProgram.get(p.ProgramId)
    if (entry) {
      if (p.City) entry.cities.add(p.City)
    } else {
      byProgram.set(p.ProgramId, {
        CardInfos: p.CardInfos,
        cities: new Set(p.City ? [p.City] : []),
      })
    }
  }

  // 2) Reconstitue des ProgramCardInfos avec LocationsString override
  return Array.from(byProgram.values()).map(({ CardInfos, cities }) => ({
    ...CardInfos,
    LocationsString: Array.from(cities).join(', '),
  }))
})

// ── Résolution des coordonnées ─────────────────────────────
async function resolveCoordinates(): Promise<void> {
  const items = props.programs?.Items ?? []
  if (!items.length) {
    points.value = []
    unlocatedPrograms.value = []
    return
  }

  isGeocoding.value = true
  const resolvedPoints: ProgramPoint[] = []

  // On aplatit (programme × adresses) en une liste de promises
  const jobs: Promise<void>[] = []
  let nextId = 0

  for (const program of items) {
    const programId = program.ProgramCardInfos.Id
    if (!programId) continue

    const addresses = program.Address ?? []

    for (const address of addresses) {
      const pointId = nextId++
      jobs.push(
        (async () => {
          try {
            const coords = await getAddressCoordinates(address)
            if (coords) {
              resolvedPoints.push({
                PointId: pointId,
                ProgramId: programId,
                CardInfos: program.ProgramCardInfos,
                City: address.City,
                Lat: coords.lat,
                Lng: coords.lng,
              })
            }
          } catch {
            /* ignoré, géré en fin */
          }
        })(),
      )
    }
  }

  await Promise.all(jobs)

  // Recalcule les programmes sans aucun point résolu
  const resolvedIds = new Set(resolvedPoints.map((p) => p.ProgramId))
  const failedPrograms: string[] = []
  for (const program of items) {
    if (!program.ProgramCardInfos.Id) continue
    if (!resolvedIds.has(program.ProgramCardInfos.Id)) {
      failedPrograms.push(program.Name)
    }
  }

  points.value = resolvedPoints
  unlocatedPrograms.value = failedPrograms
  isGeocoding.value = false
}

// ── Init Leaflet ───────────────────────────────────────────
async function initMap(): Promise<void> {
  if (!mapEl.value) return

  await loadLeafletStyles()
  const L = (await import('leaflet')).default
  await import('leaflet.markercluster')

  // ⚠️ Les CSS Leaflet doivent être déclarés dans nuxt.config.ts

  configureLeafletDefaultIcons(L)

  map.value = L.map(mapEl.value, {
    scrollWheelZoom: true,
    zoomControl: true,
  }).setView([46.603354, 1.888334], 6)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map.value)

  markerCluster.value = L.markerClusterGroup({
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    maxClusterRadius: 50,
  })
  map.value.addLayer(markerCluster.value)

  map.value.on('moveend', updateVisiblePointIds)
  map.value.on('zoomend', updateVisiblePointIds)

  setTimeout(() => map.value?.invalidateSize(), 100)
}

// ── Rendu des markers + fit bounds ─────────────────────────
async function renderMarkers(): Promise<void> {
  if (!map.value || !markerCluster.value) return

  const L = (await import('leaflet')).default

  markerCluster.value.clearLayers()

  if (!points.value.length) {
    updateVisiblePointIds()
    return
  }

  const layers: LeafletMarker[] = []
  for (const p of points.value) {
    const marker = L.marker([p.Lat, p.Lng])
    marker.bindTooltip(`${p.CardInfos.Name} — ${p.City}`, {
      direction: 'top',
      offset: [0, -8],
    })
    layers.push(marker)
  }

  markerCluster.value.addLayers(layers)

  const bounds = L.latLngBounds(points.value.map((p) => [p.Lat, p.Lng]))
  map.value.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })

  updateVisiblePointIds()
}

// ── Calcul des IDs visibles dans la viewport ───────────────
function updateVisiblePointIds(): void {
  if (!map.value) return
  const bounds = map.value.getBounds()
  const next = new Set<number>()
  for (const p of points.value) {
    if (bounds.contains([p.Lat, p.Lng])) next.add(p.PointId)
  }
  visiblePointIds.value = next
}

// ── Lifecycle ──────────────────────────────────────────────
onMounted(() => {
  const stop = watch(
    mapEl,
    async (el) => {
      if (!el) return
      stop()
      await initMap()
      await resolveCoordinates()
      await renderMarkers()
    },
    { immediate: true, flush: 'post' },
  )
})

watch(
  () => props.programs,
  async () => {
    await resolveCoordinates()
    await renderMarkers()
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (map.value) {
    map.value.off()
    map.value.remove()
    map.value = null
  }
})
</script>

<style scoped>
.programs-map-section {
  padding: var(--space-16) var(--container-pad);
  background: linear-gradient(135deg, rgba(55, 65, 85, 0.4) 0%, rgba(30, 40, 55, 0.4) 100%);
  border-radius: var(--radius-2xl);
  margin: var(--space-12) var(--container-pad);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.programs-map__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-12);
  text-align: left;
}

.programs-map__map-wrapper {
  position: relative;
  z-index: 0;
  isolation: isolate;
  width: 100%;
  height: 480px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.2);
  margin-bottom: var(--space-6);
}

.programs-map__map-wrapper :deep(.leaflet-container) {
  z-index: 0;
}

.programs-map__map {
  width: 100%;
  height: 100%;
}

.programs-map__loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--text-sm);
  font-weight: 500;
  z-index: 500;
  pointer-events: none;
}

.programs-map__list {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-6);
}

.programs-map__list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-3);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.programs-map__list-count {
  font-size: var(--text-xs);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
}

.programs-map__items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
  gap: var(--space-4);
  /* max-height: 640px;
  overflow-y: auto; */
  padding: var(--space-1);
}

.programs-map__empty {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  text-align: center;
  padding: var(--space-6);
  margin: 0;
}

.programs-map__unlocated {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.programs-map__unlocated-title {
  display: block;
  font-size: var(--text-xs);
  font-weight: 600;
  color: rgba(255, 180, 100, 0.9);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  margin-bottom: var(--space-2);
}

.programs-map__unlocated-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.programs-map__unlocated-list li {
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.6);
  padding-left: var(--space-3);
}

:deep(.leaflet-control-attribution) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 11px !important;
}

:deep(.marker-cluster) {
  background-color: rgba(3, 160, 241, 0.2) !important;
}

:deep(.marker-cluster div) {
  background-color: rgba(3, 160, 241, 0.7) !important;
  color: white !important;
  font-weight: 700 !important;
}

@media (max-width: 768px) {
  .programs-map-section {
    padding: var(--space-12) var(--container-pad);
    margin: var(--space-8) var(--container-pad);
  }

  .programs-map__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .programs-map__map-wrapper {
    height: 360px;
  }

  .programs-map__list {
    padding: var(--space-3) var(--space-4);
  }
}

@media (max-width: 640px) {
  .programs-map-section {
    padding: var(--space-8) var(--container-pad);
    margin: var(--space-6) 0;
    border-radius: var(--radius-xl);
  }

  .programs-map__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .programs-map__map-wrapper {
    height: 300px;
  }

  .programs-map__items {
    grid-template-columns: 1fr;
    max-height: 520px;
  }
}
</style>
